import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { registerMother } from '../lib/api';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';

export default function ProfileStart() {
  const { lang, t } = useLanguage();
  const { login } = useMother();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/my';

  const [form, setForm] = useState({
    name: '',
    phone: '',
    baby_birth_date: '',
    pin: '',
    consent: false,
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error(lang === 'zh' ? '请先勾选同意' : 'Please accept the consent first');
      return;
    }
    if (form.pin && !/^\d{4,6}$/.test(form.pin)) {
      toast.error(lang === 'zh' ? 'PIN 必须是 4-6 位数字' : 'PIN must be 4–6 digits');
      return;
    }
    setSaving(true);
    try {
      const res = await registerMother({
        name: form.name.trim(),
        phone: form.phone.trim(),
        baby_birth_date: form.baby_birth_date,
        pin: form.pin || null,
        language_preference: lang,
        consent_accepted: form.consent,
      });
      login(res.token, res.mother);
      toast.success(lang === 'zh' ? '档案已建立' : 'Profile created');
      navigate(next, { replace: true });
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (lang === 'zh' ? '创建失败' : 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-app py-8 sm:py-12 max-w-lg animate-fade-in-up" data-testid="profile-start-page">
      <div className="card-base p-6 sm:p-10">
        <div className="w-12 h-12 rounded-2xl bg-terracotta-light flex items-center justify-center mb-4">
          <UserPlus size={20} className="text-terracotta" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-ink mb-2">
          {t('profile_create_title')}
        </h1>
        <p className="text-sm text-ink-secondary mb-6 leading-relaxed">
          {t('profile_create_subtitle')}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_name')}</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              required
              data-testid="profile-name-input"
              className="input-base"
            />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_phone')}</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              placeholder={t('profile_phone_hint')}
              required
              data-testid="profile-phone-input"
              className="input-base"
            />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_baby_dob')}</span>
            <input
              type="date"
              value={form.baby_birth_date}
              onChange={(e) => update('baby_birth_date', e.target.value)}
              required
              data-testid="profile-dob-input"
              className="input-base"
            />
            <span className="block text-[11px] text-ink-tertiary mt-1">{t('profile_dob_hint')}</span>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{t('profile_pin')}</span>
            <input
              type="password"
              inputMode="numeric"
              pattern="\d{4,6}"
              value={form.pin}
              onChange={(e) => update('pin', e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="profile-pin-input"
              className="input-base"
            />
            <span className="block text-[11px] text-ink-tertiary mt-1">{t('profile_pin_hint')}</span>
          </label>

          <label className="flex items-start gap-3 p-4 rounded-2xl bg-subtle/60 cursor-pointer">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => update('consent', e.target.checked)}
              data-testid="profile-consent-checkbox"
              className="mt-1 w-4 h-4 accent-terracotta"
            />
            <span className="text-xs text-ink-secondary leading-relaxed">{t('profile_consent')}</span>
          </label>

          <button
            type="submit"
            disabled={saving}
            data-testid="profile-create-btn"
            className="btn-primary w-full disabled:opacity-60"
          >
            {saving ? '…' : t('profile_create_btn')}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-ink-secondary">
          {t('profile_have_account')}{' '}
          <Link
            to={`/profile/login${next !== '/my' ? `?next=${encodeURIComponent(next)}` : ''}`}
            className="text-terracotta font-medium hover:underline"
            data-testid="profile-go-login"
          >
            {t('profile_login_link')}
          </Link>
        </div>
      </div>
    </div>
  );
}
